// ============ LLM-as-a-Judge ============
// Optional 5th scoring dimension: uses another LLM to evaluate answer quality.
// This addresses the "answer format variability" fairness issue in regex-based scoring.
//
// Toggle: localStorage 'decisioncraft_use_llm_judge' = 'true' | 'false'
// Judge model: gpt-4o-mini (cheap, fast) by default
// Judge uses the same API key as the OpenAI agent (or Cloudflare env var)

import type { Task, Trace } from './types';
import { getApiKey } from './AgentBridge';

const JUDGE_KEY = 'decisioncraft_use_llm_judge';
const JUDGE_MODEL_KEY = 'decisioncraft_judge_model';

export function getUseLLMJudge(): boolean {
  try {
    return localStorage.getItem(JUDGE_KEY) === 'true';
  } catch {
    return false;
  }
}

export function setUseLLMJudge(enabled: boolean): void {
  try {
    localStorage.setItem(JUDGE_KEY, enabled ? 'true' : 'false');
  } catch { /* ignore */ }
}

export function getJudgeModel(): string {
  try {
    return localStorage.getItem(JUDGE_MODEL_KEY) || 'gpt-4o-mini';
  } catch {
    return 'gpt-4o-mini';
  }
}

export function setJudgeModel(model: string): void {
  try {
    localStorage.setItem(JUDGE_MODEL_KEY, model);
  } catch { /* ignore */ }
}

export interface JudgeScore {
  completeness: number;    // 0-10 — Does the answer address all task requirements?
  accuracy: number;         // 0-10 — Is the information plausible and internally consistent?
  reasoning_quality: number; // 0-10 — Does the trace show clear decision-making logic?
  overall: number;          // 0-100 — Normalized total
  rationale: string;       // Judge's explanation
  error?: string;           // Set if judge failed
}

const JUDGE_PROMPT = `You are an expert evaluator for AI agent decision-making tasks. Evaluate the agent's answer and trace.

TASK:
{task_description}

AGENT'S FINAL ANSWER:
{final_answer}

TRACE SUMMARY (steps taken):
{trace_summary}

Evaluate on three dimensions (0-10 each):
1. completeness: Does the answer address all task requirements? Are all constraints satisfied?
2. accuracy: Is the information plausible, internally consistent, and aligned with task context?
3. reasoning_quality: Does the trace show clear, logical decision-making (not random tool calls)?

Respond as JSON:
{"completeness": <0-10>, "accuracy": <0-10>, "reasoning_quality": <0-10>, "rationale": "<one sentence>"}`;

function isProduction(): boolean {
  try {
    return typeof window !== 'undefined' && window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1';
  } catch {
    return false;
  }
}

async function callJudgeAPI(body: any, apiKey: string): Promise<Response> {
  if (isProduction()) {
    return fetch('/api/agent/openai', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Api-Key': apiKey },
      body: JSON.stringify(body),
    });
  }
  return fetch('https://api.openai.com/v1/chat/completions', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${apiKey}` },
    body: JSON.stringify(body),
  });
}

export async function calculateLLMJudgeScore(trace: Trace, task: Task): Promise<JudgeScore> {
  const apiKey = getApiKey('openai');
  if (!apiKey) {
    return {
      completeness: 0, accuracy: 0, reasoning_quality: 0, overall: 0,
      rationale: 'LLM Judge skipped: no OpenAI API key configured',
      error: 'no_api_key',
    };
  }

  // Build trace summary (compact)
  const traceSummary = (trace.steps ?? []).slice(0, 20).map((s, i) => {
    const action = s.action;
    if (action.type === 'tool_call') {
      return `${i + 1}. [tool_call] ${action.tool}(${JSON.stringify(action.args ?? {}).slice(0, 80)}) → ${s.observation?.status ?? '?'}`;
    }
    if (action.type === 'thinking') {
      return `${i + 1}. [thinking] ${(action.reasoning ?? '').slice(0, 100)}`;
    }
    if (action.type === 'finish') {
      return `${i + 1}. [finish] ${(action.reasoning ?? '').slice(0, 100)}`;
    }
    return `${i + 1}. [${action.type}]`;
  }).join('\n');

  const finalAnswerStr = typeof trace.final_answer === 'string'
    ? trace.final_answer
    : JSON.stringify(trace.final_answer ?? {}).slice(0, 1500);

  const prompt = JUDGE_PROMPT
    .replace('{task_description}', task.description.slice(0, 500))
    .replace('{final_answer}', finalAnswerStr)
    .replace('{trace_summary}', traceSummary);

  const model = getJudgeModel();

  try {
    const response = await callJudgeAPI({
      model,
      messages: [
        { role: 'system', content: 'You are an expert evaluator. Respond only with JSON.' },
        { role: 'user', content: prompt },
      ],
      temperature: 0,
      max_tokens: 200,
      response_format: { type: 'json_object' },
    }, apiKey);

    if (!response.ok) {
      const errText = await response.text().catch(() => '');
      return {
        completeness: 0, accuracy: 0, reasoning_quality: 0, overall: 0,
        rationale: `Judge API error ${response.status}`,
        error: errText.slice(0, 200),
      };
    }

    const data = await response.json();
    const content = data.choices?.[0]?.message?.content ?? '{}';
    let parsed: any;
    try {
      parsed = JSON.parse(content);
    } catch {
      // Try to extract JSON from text
      const m = content.match(/\{[\s\S]*\}/);
      parsed = m ? JSON.parse(m[0]) : {};
    }

    const completeness = Math.max(0, Math.min(10, Number(parsed.completeness) || 0));
    const accuracy = Math.max(0, Math.min(10, Number(parsed.accuracy) || 0));
    const reasoning = Math.max(0, Math.min(10, Number(parsed.reasoning_quality) || 0));
    // Normalize: each dimension contributes equally to overall (0-100)
    const overall = Math.round(((completeness + accuracy + reasoning) / 30) * 100);

    return {
      completeness,
      accuracy,
      reasoning_quality: reasoning,
      overall,
      rationale: String(parsed.rationale ?? ''),
    };
  } catch (err) {
    return {
      completeness: 0, accuracy: 0, reasoning_quality: 0, overall: 0,
      rationale: 'Judge call failed',
      error: err instanceof Error ? err.message : 'Unknown error',
    };
  }
}
